<template>
    <router-link :to="link" class="entity-card" :class="`entity-card--${kind}`">
        <div class="entity-card__media">
            <img
                v-if="imageSrc && !imageFailed"
                :src="imageSrc"
                :alt="entity.title"
                loading="lazy"
                @error="imageFailed = true"
            >
            <img
                v-else
                class="entity-card__placeholder"
                :src="require('@/assets/LOGOPARON 2023.svg')"
                alt=""
            >
            <span v-if="category" class="entity-card__chip">{{ category }}</span>
        </div>

        <div class="entity-card__body">
            <h3 class="entity-card__title">{{ entity.title }}</h3>
            <div v-if="entity.type" class="entity-card__subtitle">{{ entity.type }}</div>

            <div v-if="kind === 'partner'" class="entity-card__offer" :class="{ 'entity-card__offer--generic': !hasRealOffer }">
                <v-icon small :color="hasRealOffer ? '#ad282d' : '#1b2f5a'">{{ hasRealOffer ? 'mdi-ticket-percent-outline' : 'mdi-tag-outline' }}</v-icon>
                <span>{{ hasRealOffer ? offerText : 'Δείτε τις εκπτώσεις για τα μέλη' }}</span>
            </div>

            <div v-if="entity.address" class="entity-card__address">
                <v-icon small color="#1b2f5a">mdi-map-marker-outline</v-icon>
                <span>{{ entity.address }}</span>
            </div>
        </div>

        <div class="entity-card__footer">
            <span>Λεπτομέρειες</span>
            <v-icon small color="#ad282d">mdi-arrow-right</v-icon>
        </div>
    </router-link>
</template>

<script>
import restApi from '@/rest-api'

// Card for a partner (business) or a team (club) in the homepage grids and the listing pages
export default {
    name: 'EntityCard',
    props: {
        entity: { type: Object, required: true },
        kind: {
            type: String,
            default: 'partner',
            validator: value => ['partner', 'team'].includes(value)
        }
    },
    data: () => ({
        imageFailed: false
    }),
    computed: {
        link() {
            return this.kind === 'partner' ? `/Partner?id=${this.entity.id}` : `/Team?id=${this.entity.id}`
        },
        imageSrc() {
            return restApi.imageUrl(this.entity.image)
        },
        category() {
            const category = this.entity.category
            return category && typeof category === 'object' ? category.title : category
        },
        offerText() {
            // public, active offers first
            const offers = (this.entity.offers || []).filter(offer => offer && offer.description)
            const offer = offers.find(item => item.active && !item.secret) || offers[0]
            return offer ? offer.description : ''
        },
        // many partners carry a placeholder like "Κάντε κλικ για να δείτε όλες τις εκπτώσεις":
        // that is not an offer, so it gets the quiet style instead of the red highlight
        hasRealOffer() {
            return !!this.offerText && !/κλικ|click|^\s*δε[ίι]τε/i.test(this.offerText)
        }
    },
    watch: {
        'entity.id'() {
            this.imageFailed = false
        }
    }
}
</script>

<style scoped>
.entity-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.12);
    border-radius: 14px;
    overflow: hidden;
    color: #1b2f5a !important;
    text-decoration: none;
    box-shadow: 0 1px 2px rgba(27, 47, 90, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.entity-card:hover,
.entity-card:focus-visible {
    transform: translateY(-4px);
    border-color: rgba(173, 40, 45, 0.35);
    box-shadow: 0 12px 28px rgba(27, 47, 90, 0.14);
}

.entity-card__media {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 176px;
    padding: 44px 32px 16px;
    background: linear-gradient(180deg, #f7f8fb 0%, #eef1f7 100%);
    border-bottom: 3px solid #ad282d;
}

.entity-card__media img {
    width: 100%;
    max-width: 220px;
    height: 100%;
    object-fit: contain;
}

.entity-card__media img.entity-card__placeholder {
    width: 84px;
    opacity: 0.35;
}

.entity-card__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    max-width: calc(100% - 24px);
    padding: 3px 10px;
    border-radius: 999px;
    background: #1b2f5a;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.entity-card__body {
    flex: 1;
    padding: 16px 18px 8px;
}

.entity-card__title {
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.entity-card__subtitle {
    margin-top: 4px;
    font-size: 14px;
    color: rgba(27, 47, 90, 0.7);
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.entity-card__offer {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(173, 40, 45, 0.07);
    color: #8f1f23;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
}

.entity-card__offer--generic {
    background: #f2f4f8;
    color: #1b2f5a;
    font-weight: 500;
}

.entity-card__offer span,
.entity-card__address span {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.entity-card__address {
    display: flex;
    gap: 6px;
    align-items: flex-start;
    margin-top: 12px;
    font-size: 13px;
    color: rgba(27, 47, 90, 0.75);
    line-height: 1.4;
}

.entity-card__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding: 10px 18px 14px;
    color: #ad282d;
    font-size: 14px;
    font-weight: 600;
}

.entity-card--team .entity-card__body {
    text-align: center;
}

.entity-card--team .entity-card__address {
    justify-content: center;
}

/* phones: logo on the left, text on the right, about half the height */
@media (max-width: 599px) {
    .entity-card {
        display: grid;
        grid-template-columns: 112px 1fr;
        grid-template-rows: 1fr auto;
    }

    .entity-card__media {
        grid-row: 1 / span 2;
        height: auto;
        min-height: 128px;
        padding: 12px;
        border-bottom: 0;
        border-right: 3px solid #ad282d;
    }

    .entity-card__chip {
        display: none;
    }

    .entity-card__body {
        padding: 12px 14px 4px;
    }

    .entity-card__title {
        font-size: 16px;
    }

    .entity-card__subtitle {
        font-size: 13px;
    }

    .entity-card__offer {
        margin-top: 8px;
        padding: 6px 8px;
        font-size: 13px;
    }

    .entity-card__address {
        display: none;
    }

    .entity-card__footer {
        padding: 4px 14px 10px;
        font-size: 13px;
    }

    .entity-card--team .entity-card__body {
        text-align: left;
    }
}
</style>
